import React, { useEffect, useRef } from "react";
import { motion } from "framer-motion";

// Nariz de payaso: elemento de identidad Cometa
export const ClownNose = ({ size = 40, className = "", pulse = false }) => (
  <span
    aria-hidden="true"
    data-testid="clown-nose"
    className={`inline-block rounded-full shrink-0 ${pulse ? "animate-float" : ""} ${className}`}
    style={{
      width: size,
      height: size,
      background: "radial-gradient(circle at 32% 28%, #ff7a7a 0%, #E01F26 45%, #9e0f14 100%)",
      boxShadow: `inset -${size * 0.08}px -${size * 0.1}px ${size * 0.25}px rgba(0,0,0,0.25), 0 ${size * 0.15}px ${size * 0.35}px rgba(224,31,38,0.35)`,
    }}
  />
);

export const PORTRAIT_1 = "/img/p1.webp";
export const PORTRAIT_2 = "/img/p2.webp";
export const ARA_BN = "/img/ara_bn.webp";
export const FOTO_PRINCIPAL = "/img/principal.webp";
export const FOTOS = {
  kapital1: "/img/kapital1.webp",
  kapital2: "/img/kapital2.webp",
  payasos: "/img/payasos.webp",
  alvaro: "/img/alvaro.webp",
};

// Trazo "cometa" (curva + estrella) inspirado en el elemento del banner oficial. Se usa como fondo decorativo.
export const CometaSwoosh = ({ className = "", color = "#FFFFFF", opacity = 0.18 }) => (
  <svg aria-hidden="true" data-testid="cometa-swoosh" viewBox="0 0 800 400" fill="none" className={`pointer-events-none ${className}`} style={{ opacity }} preserveAspectRatio="xMidYMid slice">
    <motion.path d="M-300 200 C -100 60, 200 20, 260 110 C 300 200, 140 330, 60 340 C -20 350, -10 250, 120 260 C 260 270, 420 330, 560 300 C 700 270, 900 150, 1200 -80" stroke={color} strokeWidth="22" strokeLinecap="round" initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true, amount: 0.2 }} transition={{ duration: 2.2, ease: "easeInOut" }} />
  </svg>
);

// Fondo vivo: círculos flotantes + estrella fugaz, sigue suavemente al cursor
export const LiveBackground = ({ className = "", tone = "light" }) => {
  const ref = useRef(null);
  useEffect(() => {
    const onMove = (e) => {
      const el = ref.current; if (!el) return;
      const r = el.getBoundingClientRect();
      if (e.clientY < r.top || e.clientY > r.bottom) return;
      el.style.setProperty("--mx", `${((e.clientX - r.left) / r.width - 0.5) * 40}px`);
      el.style.setProperty("--my", `${((e.clientY - r.top) / r.height - 0.5) * 40}px`);
    };
    window.addEventListener("mousemove", onMove, { passive: true });
    return () => window.removeEventListener("mousemove", onMove);
  }, []);
  const blobs = tone === "dark"
    ? [["#45B2FD", 0.25], ["#FFBC01", 0.18], ["#E01F26", 0.14]]
    : [["#FFBC01", 0.35], ["#7ABAF2", 0.35], ["#E01F26", 0.12]];
  return (
    <div ref={ref} aria-hidden="true" data-testid="live-bg" className={`absolute inset-0 overflow-hidden pointer-events-none [&>*]:pointer-events-none ${className}`} style={{ "--mx": "0px", "--my": "0px" }}>
      {blobs.map(([c, o], i) => (
        <motion.span key={i} className="absolute rounded-full blur-3xl" style={{ background: c, opacity: o, width: 320 + i * 120, height: 320 + i * 120, left: `${[5, 60, 35][i]}%`, top: `${[10, 30, 70][i]}%`, translate: `calc(var(--mx) * ${i + 1}) calc(var(--my) * ${i + 1})`, transition: "translate 1.2s ease-out" }}
          animate={{ x: [0, 40 + i * 20, -30, 0], y: [0, -30, 40 + i * 10, 0], scale: [1, 1.12, 0.94, 1] }} transition={{ duration: 18 + i * 6, repeat: Infinity, ease: "easeInOut" }} />
      ))}
      <motion.span className="absolute h-1 w-40 rounded-full bg-gradient-to-r from-transparent via-white to-white/0" style={{ top: "18%", left: "-10%", rotate: 20 }} animate={{ x: ["0vw", "130vw"], opacity: [0, 1, 0] }} transition={{ duration: 4, repeat: Infinity, repeatDelay: 7, ease: "easeIn" }} />
    </div>
  );
};

const Wordmark = ({ light = true, className = "" }) => (
  <div className={`leading-none select-none ${className}`} style={{ color: light ? "#FBF7F0" : "#0B1B2E" }}>
    <div className="flex items-end gap-1">
      <span className="font-script font-bold text-4xl md:text-5xl lg:text-6xl">Dra</span>
      <span className="cometa-star" style={{ width: "1.2rem", height: "1.2rem", marginBottom: 14 }} />
    </div>
    <div className="font-display font-black text-5xl md:text-6xl lg:text-8xl tracking-tight" style={{ letterSpacing: "-0.04em" }}>COMETA</div>
    <div className="mt-3 text-[0.65rem] md:text-xs tracking-[0.4em] opacity-80">LIC. ARACELI LUGO</div>
  </div>
);

// Banner de identidad en colores institucionales (variantes distintas para no repetir)
export const CometaBanner = ({ variant = "full", className = "" }) => {
  if (variant === "portrait") {
    return (
      <div className={`relative overflow-hidden bg-cometa-celeste rounded-[2rem] ${className}`} data-testid="cometa-banner-portrait">
        <CometaSwoosh className="absolute inset-0 w-full h-full" color="#004B8D" opacity={0.2} />
        <img loading="lazy" decoding="async" src={ARA_BN} alt="Araceli Lugo" className="relative w-full h-full object-cover object-top grayscale" />
      </div>
    );
  }
  if (variant === "strip") {
    return (
      <div className={`relative overflow-hidden bg-cometa-carmesi text-white rounded-[2rem] ${className}`} data-testid="cometa-banner-strip">
        <CometaSwoosh className="absolute -left-10 top-0 w-[70%] h-full" opacity={0.22} />
        <div className="relative grid md:grid-cols-2 items-end gap-6 px-8 pt-10">
          <div className="pb-10">
            <p className="font-script text-4xl md:text-5xl text-cometa-dorado mb-2">¿Cuál es tu utopía?</p>
            <p className="text-lg md:text-xl opacity-90 max-w-md">Compártela y conviértela en una estrella de nuestra constelación.</p>
          </div>
          <img loading="lazy" decoding="async" src={ARA_BN} alt="Araceli Lugo" className="w-full max-w-xs mx-auto md:mx-0 md:ml-auto object-cover object-top rounded-t-3xl grayscale" />
        </div>
      </div>
    );
  }
  return (
    <div className={`relative overflow-hidden bg-[#FECD03] text-cometa-tinta ${className}`} data-testid="cometa-banner-full">
      <div className="relative max-w-6xl mx-auto px-6 grid md:grid-cols-2 items-end gap-6">
        <div className="relative w-full max-w-sm md:max-w-md mx-auto md:mx-0 pt-10">
          <img fetchPriority="high" decoding="async" src={FOTO_PRINCIPAL} alt="Dra. Cometa — Lic. Araceli Lugo" className="relative z-10 w-full object-contain object-bottom block" />
          <svg aria-hidden="true" viewBox="0 0 1643 1643" overflow="visible" className="absolute left-full top-10 h-[calc(100%-2.5rem)] aspect-square pointer-events-none" style={{ overflow: "visible" }}>
            <path d="M-90 997 C 400 765, 900 500, 6000 -100" stroke="#45B2FD" strokeWidth="84" strokeLinecap="butt" fill="none" />
          </svg>
          <svg aria-hidden="true" viewBox="0 0 1643 1643" overflow="visible" className="absolute right-full top-10 h-[calc(100%-2.5rem)] aspect-square pointer-events-none" style={{ overflow: "visible" }}>
            <path d="M1733 1148 C 1200 1262, 400 1340, -6000 1750" stroke="#45B2FD" strokeWidth="80" strokeLinecap="butt" fill="none" />
          </svg>
        </div>
        <div className="relative z-10 pb-12 md:pb-16 text-center md:text-left flex flex-col items-center md:items-start">
          <Wordmark light={false} />
        </div>
      </div>
    </div>
  );
};
